import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Building2,
  Check,
  Loader2,
  Plus,
  Tags,
  Trash2,
  X,
} from 'lucide-react';
import {
  fetchReferentiels,
  createRoleBureau,
  updateRoleBureau,
  deleteRoleBureau,
  createSecteur,
  updateSecteur,
  deleteSecteur,
  type ReferentielItem,
} from '../services/api';

type Referentiel = 'roles' | 'secteurs';

/**
 * Gestion des deux tables de référence : fonctions au bureau et postes tenus
 * par les bénévoles. Elles n'étaient jusqu'ici alimentées que par une commande
 * console, ce qui obligeait à passer par un développeur.
 *
 * Le second onglet s'appelait « Secteurs d'activité », ce que son contenu
 * dément : accueil, bar, snack, montage, démontage sont des postes tenus sur
 * un événement, pas des branches d'activité économique. Le nom technique de la
 * table reste ActivitySector — le renommer n'apporterait rien au comité et
 * couperait l'historique.
 */
export default function GestionReferentiels() {
  const queryClient = useQueryClient();
  const [onglet, setOnglet] = useState<Referentiel>('roles');
  const [nouveau, setNouveau] = useState('');
  const [notice, setNotice] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const query = useQuery({ queryKey: ['referentiels'], queryFn: fetchReferentiels });

  const rafraichir = () => queryClient.invalidateQueries({ queryKey: ['referentiels'] });
  const reussite = (message: string) => {
    setNotice({ type: 'success', message });
    setNouveau('');
    rafraichir();
  };
  const echec = (error: Error) => setNotice({ type: 'error', message: error.message });

  const creer = useMutation({
    mutationFn: () =>
      onglet === 'roles'
        ? createRoleBureau({ label: nouveau.trim() })
        : createSecteur({ label: nouveau.trim() }),
    onSuccess: (d) => reussite(d.message),
    onError: echec,
  });

  const basculer = useMutation({
    mutationFn: ({ id, actif }: { id: number; actif: boolean }) =>
      onglet === 'roles' ? updateRoleBureau(id, { actif }) : updateSecteur(id, { actif }),
    onSuccess: (d) => reussite(d.message),
    onError: echec,
  });

  const renommer = useMutation({
    mutationFn: ({ id, label }: { id: number; label: string }) =>
      onglet === 'roles' ? updateRoleBureau(id, { label }) : updateSecteur(id, { label }),
    onSuccess: (d) => reussite(d.message),
    onError: echec,
  });

  const supprimer = useMutation({
    mutationFn: (id: number) => (onglet === 'roles' ? deleteRoleBureau(id) : deleteSecteur(id)),
    onSuccess: (d) => reussite(d.message),
    onError: echec,
  });

  const items: ReferentielItem[] =
    (onglet === 'roles' ? query.data?.rolesBureau : query.data?.secteurs) ?? [];

  // Le titre et la carte sont portés par SectionParametres, qui replie
  // l'ensemble : les répéter ici ferait deux en-têtes l'un sur l'autre.
  return (
    <div className="space-y-4">
      {notice && (
        <div
          className={`flex items-start gap-2 text-sm rounded-lg p-3 ${
            notice.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}
        >
          {notice.type === 'success' ? (
            <Check className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <p className="flex-1">{notice.message}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      <div className="flex items-center gap-2 border-b border-gray-200">
        {([
          { value: 'roles', label: 'Fonctions au bureau', icone: Building2 },
          { value: 'secteurs', label: 'Postes bénévoles', icone: Tags },
        ] as const).map((o) => (
          <button
            key={o.value}
            onClick={() => {
              setOnglet(o.value);
              setNotice(null);
              setNouveau('');
            }}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${
              onglet === o.value ? 'border-primary-600 text-primary-700' : 'border-transparent text-gray-500 dark:text-gray-400'
            }`}
          >
            <o.icone className="w-4 h-4 inline mr-1" /> {o.label}
          </button>
        ))}
      </div>

      {/* La grille des droits vivait ici, dans un onglet. Elle a rejoint
          l'écran des droits, avec les statuts et l'administration du site :
          les trois se lisaient séparément et se contredisaient — on pouvait
          tout cocher pour une fonction sans que la personne y gagne rien. */}
      <>
      <div className="flex gap-2">
        <input
          className="input flex-1"
          placeholder={onglet === 'roles' ? 'Nouvelle fonction, ex. Vice-Secrétaire' : 'Nouveau poste, ex. Buvette'}
          value={nouveau}
          onChange={(e) => setNouveau(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && nouveau.trim()) creer.mutate();
          }}
        />
        <button
          onClick={() => creer.mutate()}
          disabled={creer.isPending || !nouveau.trim()}
          className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
        >
          {creer.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Ajouter
        </button>
      </div>

      {query.isPending ? (
        <Loader2 className="w-6 h-6 animate-spin text-primary-600" />
      ) : (
        <ul className="divide-y divide-gray-100">
          {items.map((item) => (
            // La clé porte l'onglet, et pas seulement l'identifiant.
            //
            // Les deux tables numérotent leurs lignes chacune de son côté :
            // la fonction n° 1 est « Président », le poste n° 1 « Accueil ».
            // Sur la seule clé `item.id`, React reconnaissait la même ligne
            // d'un onglet à l'autre et gardait le champ de saisie en place —
            // or il n'est pas contrôlé, il affichait donc encore « Président »
            // alors que la ligne était devenue « Accueil ». Le premier clic
            // ailleurs enregistrait ce libellé périmé sur le poste : c'est
            // ainsi que « Accueil » s'est retrouvé nommé « Président » en
            // production.
            <li key={`${onglet}-${item.id}`} className="py-3 flex flex-wrap items-center gap-3">
              <input
                className="input flex-1 min-w-[12rem]"
                defaultValue={item.label}
                onBlur={(e) => {
                  const label = e.target.value.trim();
                  if (label && label !== item.label) renommer.mutate({ id: item.id, label });
                }}
              />
              <code className="text-xs text-gray-400">{item.code}</code>

              {item.dirigeante && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">
                  Accès aux modules
                </span>
              )}
              {item.utilisations !== undefined && item.utilisations > 0 && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 dark:text-gray-300 dark:bg-gray-800">
                  {item.utilisations} membre{item.utilisations > 1 ? 's' : ''}
                </span>
              )}

              <button
                onClick={() => basculer.mutate({ id: item.id, actif: !item.actif })}
                className={`text-xs px-2 py-1 rounded-full font-medium ${
                  item.actif ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600 dark:text-gray-400 dark:bg-gray-700'
                }`}
                title={item.actif ? 'Masquer des formulaires' : 'Rendre à nouveau proposable'}
              >
                {item.actif ? 'Actif' : 'Inactif'}
              </button>

              <button
                onClick={() => {
                  if (window.confirm(`Supprimer définitivement « ${item.label} » ?`)) {
                    supprimer.mutate(item.id);
                  }
                }}
                disabled={supprimer.isPending}
                className="text-red-600 hover:text-red-700"
                aria-label={`Supprimer ${item.label}`}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </li>
          ))}
          {items.length === 0 && (
            <li className="py-3 text-sm text-gray-500 dark:text-gray-400">Aucune entrée pour le moment.</li>
          )}
        </ul>
      )}

      <p className="text-xs text-gray-500 flex items-start gap-1 dark:text-gray-400">
        <X className="w-3 h-3 mt-0.5 shrink-0" />
        Une entrée déjà attribuée ne peut pas être supprimée : désactivez-la, l'historique
        des fiches et des plannings reste ainsi intact.
      </p>

      {/* « DevOps » a figuré dans cette liste avant d'etre retire de toutes
          les instances : ce n'etait pas une fonction au bureau. La tenue
          technique du site releve du support de la plateforme, qui n'a pas
          de fiche ici — la note evite qu'on recree ce role faute de le
          trouver. */}
      {onglet === 'roles' && (
        <p className="text-xs text-gray-500 flex items-start gap-1 dark:text-gray-400">
          <X className="w-3 h-3 mt-0.5 shrink-0" />
          La tenue technique du site relève du support de la plateforme, pas d'une fonction
          au bureau : inutile de créer un rôle « informatique » ou « DevOps » ici.
        </p>
      )}
      </>
    </div>
  );
}
